import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { GitPullRequest, Loader2, Paperclip, X, FileUp } from "lucide-react";

const CO_STATUS = {
  open: "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10",
  acknowledged: "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10",
  amended: "text-[#B084FF] border-[#B084FF]/30 bg-[#B084FF]/10",
  bounced: "text-[#FF5C5C] border-[#FF5C5C]/30 bg-[#FF5C5C]/10",
  signed: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10",
};
const CO_LABEL = { open: "open", acknowledged: "acknowledged", amended: "amended", bounced: "bounced", signed: "signed" };
const DELTA_KINDS = [
  { key: "hld", label: "HLD" },
  { key: "lld", label: "LLD" },
  { key: "diagram", label: "Diagram" },
];

export const ChangeOrdersPanel = ({ projectId, ccOpportunityId }) => {
  const [cos, setCos] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null);
  const [upBusy, setUpBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/change-orders`); setCos(data); }
    catch { /* silent */ }
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);

  if (!ccOpportunityId) return null;

  const uploadDelta = async (co, kind, fileInput) => {
    const file = fileInput.files?.[0];
    if (!file) return;
    setUpBusy(`${co.co_seq}-${kind}`);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await api.post(`/projects/${projectId}/change-orders/${co.co_seq}/delta-upload`, fd);
      toast.success(`${kind.toUpperCase()} delta attached to CO-${co.co_seq}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUpBusy(null); fileInput.value = ""; }
  };

  const removeDelta = async (co, kind) => {
    setUpBusy(`${co.co_seq}-${kind}`);
    try {
      await api.delete(`/projects/${projectId}/change-orders/${co.co_seq}/delta/${kind}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    finally { setUpBusy(null); }
  };

  const respond = async (co, status) => {
    setBusy(co.co_seq);
    try {
      await api.post(`/projects/${projectId}/change-orders/${co.co_seq}/respond`, {
        status, engineering_note: draft[co.co_seq]?.note || "",
        rejection_reason: status === "bounced" ? (draft[co.co_seq]?.note || "") : "",
      });
      toast.success(`Change order CO-${co.co_seq} ${status} — Command Centre notified.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="mt-8 border border-white/10 bg-[#0B0C10] p-6" data-testid="change-orders-panel">
      <div className="font-mono-jb text-lg font-bold text-white flex items-center gap-2 mb-1">
        <GitPullRequest size={18} className="text-[#B084FF]" /> Change Orders
      </div>
      <p className="text-[11px] text-[#8B949E] font-mono-jb mb-4">Scope-change requests raised in Command Centre. Respond so CC is notified (change_order_ack).</p>
      {cos.length === 0 ? (
        <div className="text-[#484F58] font-mono-jb text-xs py-3">No change orders for this project.</div>
      ) : (
        <div className="space-y-3">
          {cos.map((co) => (
            <div key={co.co_seq} data-testid={`co-${co.co_seq}`} className="border border-white/10 p-4 bg-[#07080B]">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono-jb text-sm text-white">CO-{co.co_seq}: {co.title || "(untitled)"}</span>
                <span className={`text-[9px] uppercase tracking-wider font-mono-jb px-1.5 py-0.5 rounded-sm border ${CO_STATUS[co.status] || CO_STATUS.open}`}>{CO_LABEL[co.status] || "unknown"}</span>
              </div>
              {co.reason && <div className="text-[11px] text-[#8B949E] font-mono-jb mt-1">{co.reason}</div>}
              {co.commercial_delta_summary && <div className="text-[11px] text-[#8B949E] font-mono-jb mt-1">Commercial: {co.commercial_delta_summary}</div>}
              {(co.bom_delta?.added?.length || co.bom_delta?.removed?.length || co.bom_delta?.modified?.length) ? (
                <div className="text-[10px] text-[#484F58] font-mono-jb mt-1">BoM delta: +{co.bom_delta?.added?.length || 0} / -{co.bom_delta?.removed?.length || 0} / ~{co.bom_delta?.modified?.length || 0}</div>
              ) : null}
              {co.response?.status && <div className="text-[11px] text-[#00F0FF] font-mono-jb mt-2">Response: {co.response.status}{co.response.engineering_note ? ` — ${co.response.engineering_note}` : ""}</div>}
              {co.status !== "open" && co.deltas && Object.keys(co.deltas).length > 0 && (
                <div data-testid={`co-deltas-readonly-${co.co_seq}`} className="mt-2 flex flex-wrap gap-2">
                  {DELTA_KINDS.filter(({ key }) => co.deltas?.[key]).map(({ key, label }) => (
                    <span key={key} className="flex items-center gap-1 border border-white/10 bg-[#0B0C10] px-2 py-1 text-[10px] font-mono-jb text-[#8B949E]">
                      <Paperclip size={10} className="text-[#B084FF]" />{label}: <span className="text-[#00FF41] max-w-[140px] truncate" title={co.deltas[key].filename}>{co.deltas[key].filename}</span>
                    </span>
                  ))}
                </div>
              )}
              {co.status === "open" && (
                <div className="mt-3 border-t border-white/5 pt-3">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#C9D1D9] font-mono-jb mb-2">
                    <Paperclip size={11} className="text-[#B084FF]" /> Revised design deltas (sent to CC)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {DELTA_KINDS.map(({ key, label }) => {
                      const d = co.deltas?.[key];
                      const bkey = `${co.co_seq}-${key}`;
                      return (
                        <div key={key} data-testid={`co-delta-${co.co_seq}-${key}`} className="flex items-center gap-1.5 border border-white/10 bg-[#0B0C10] px-2 py-1.5">
                          <span className="text-[10px] font-mono-jb text-[#C9D1D9] min-w-[42px]">{label}</span>
                          {d ? (
                            <>
                              <span className="text-[10px] font-mono-jb text-[#00FF41] max-w-[140px] truncate" title={d.filename}>{d.filename}</span>
                              <button data-testid={`co-delta-remove-${co.co_seq}-${key}`} disabled={upBusy === bkey} onClick={() => removeDelta(co, key)} className="text-[#FF5C5C] hover:text-[#FF8080] disabled:opacity-40" title="Remove">
                                {upBusy === bkey ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                              </button>
                            </>
                          ) : (
                            <label className="flex items-center gap-1 text-[10px] font-mono-jb text-[#00F0FF] cursor-pointer hover:text-white">
                              {upBusy === bkey ? <Loader2 size={12} className="animate-spin" /> : <FileUp size={12} />} Attach
                              <input data-testid={`co-delta-input-${co.co_seq}-${key}`} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg,.svg,.vsdx" className="hidden"
                                onChange={(e) => uploadDelta(co, key, e.target)} disabled={upBusy === bkey} />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Textarea data-testid={`co-note-${co.co_seq}`} rows={2} placeholder="Engineering note / rejection reason..."
                    value={draft[co.co_seq]?.note || ""} onChange={(e) => setDraft({ ...draft, [co.co_seq]: { note: e.target.value } })}
                    className="bg-[#0B0C10] border-white/15 font-mono-jb text-xs mt-3" />
                  <div className="flex gap-2 mt-2">
                    <Button data-testid={`co-ack-${co.co_seq}`} disabled={busy === co.co_seq} onClick={() => respond(co, "acknowledged")} className="h-8 rounded-sm bg-[#00F0FF] text-black font-mono-jb text-[10px] uppercase tracking-wider hover:bg-[#00F0FF]">{busy === co.co_seq ? <Loader2 size={12} className="animate-spin" /> : "Acknowledge"}</Button>
                    <Button data-testid={`co-amend-${co.co_seq}`} disabled={busy === co.co_seq} onClick={() => respond(co, "amended")} variant="outline" className="h-8 rounded-sm bg-transparent border-[#B084FF]/40 text-[#B084FF] font-mono-jb text-[10px] uppercase tracking-wider hover:bg-[#B084FF]/10">Amend</Button>
                    <Button data-testid={`co-bounce-${co.co_seq}`} disabled={busy === co.co_seq} onClick={() => respond(co, "bounced")} variant="outline" className="h-8 rounded-sm bg-transparent border-[#FF3B30]/40 text-[#FF5C5C] font-mono-jb text-[10px] uppercase tracking-wider hover:bg-[#FF3B30]/10">Bounce</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChangeOrdersPanel;
